import { CountryCode, getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js/max';
import { ServiceFormDefinitionDto, ServiceFormFieldDto } from '../../general/dtos';

export interface MessengerPhoneValue { country?: CountryCode; number: string; }
export type BookingValues = Record<string, string | MessengerPhoneValue>;

export function fieldText(text: string, field: ServiceFormFieldDto, definition: ServiceFormDefinitionDto, values: BookingValues): string {
    const method = definition.fields.find(candidate => candidate.key === field.contactMethodField);
    const selected = method?.options?.find(option => option.value === values[method.key])?.label ?? 'your selected messenger';
    return text.replace(/\{contactMethod\}/g, selected);
}

export function validateBooking(definition: ServiceFormDefinitionDto, values: BookingValues) {
    const errors: Record<string, string> = {};
    const answers: Record<string, string> = {};
    for (const field of definition.fields) {
        const input = values[field.key];
        if (field.type === 'messenger-phone') {
            const phone = input && typeof input !== 'string' ? input : { number: '' };
            if (!phone.number.trim()) {
                if (field.required) errors[field.key] = 'Enter the phone number registered with your selected messenger.';
                answers[field.key] = '';
                continue;
            }
            if (!phone.country) { errors[field.key] = 'Select the country calling code for this phone number.'; continue; }
            if (!/^[+\d\s().-]+$/.test(phone.number)) { errors[field.key] = 'Enter a valid phone number.'; continue; }
            const parsed = parsePhoneNumberFromString(phone.number, { defaultCountry: phone.country, extract: false });
            if (!parsed?.isValid() || parsed.countryCallingCode !== getCountryCallingCode(phone.country)) {
                errors[field.key] = 'Enter a valid phone number for the selected country calling code.';
            } else if (field.maxLength && parsed.number.length > field.maxLength) {
                errors[field.key] = `Use no more than ${field.maxLength} characters for the international phone number.`;
            } else answers[field.key] = parsed.number;
            continue;
        }
        const raw = typeof input === 'string' ? input : '';
        const value = raw.trim();
        answers[field.key] = value;
        if (field.required && !value) errors[field.key] = 'This field is required.';
        else if (field.maxLength && raw.length > field.maxLength) errors[field.key] = `Use no more than ${field.maxLength} characters.`;
        else if (value && field.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) errors[field.key] = 'Enter a valid email address.';
        else if (value && field.type === 'radio' && !field.options?.some(option => option.value === value)) errors[field.key] = 'Select one of the available options.';
    }
    return { errors, answers };
}

export function updateBookingValue(definition: ServiceFormDefinitionDto, values: BookingValues, key: string, value: string | MessengerPhoneValue): BookingValues {
    const next = { ...values, [key]: value };
    if (values[key] !== value) {
        for (const field of definition.fields) {
            if (field.type === 'messenger-phone' && field.contactMethodField === key) {
                const previous = values[field.key];
                next[field.key] = { country: typeof previous === 'object' ? previous.country : undefined, number: '' };
            }
        }
    }
    return next;
}
